import { buttonVariants } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/utils';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as React from 'react';

/**
 * Figma 라이브러리 Pop-Up (848:9402) — 확인 · 경고 팝업 (로그아웃 확인 870:11057 등)
 * - 카드: 폭 328 · radius 40 · 1.5px grey-100 테두리 · 위아래 40 / 좌우 24 · 가운데 정렬
 * - 제목 16 Bold · 본문 14 Medium grey-500 (간격 8) → 버튼까지 24
 * - 버튼: Large(48 · radius 16). 가로 배치(기본)는 반반, layout="vertical"이면
 *   위 primary Large 전체 폭 + 아래 Text/black Medium (Figma Vertical Buttons, 아래 여백 24)
 * - 딤: 검정 50% (Color/Transparency/Black/Black-50%)
 * 모바일에서도 중앙 카드로 띄운다.
 */
const AlertDialog = AlertDialogPrimitive.Root;
const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Portal>
    <AlertDialogPrimitive.Overlay className="fixed inset-0 z-50 bg-dim-50" />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed top-1/2 left-1/2 z-50 flex w-[calc(100%-32px)] max-w-[328px] -translate-x-1/2 -translate-y-1/2 flex-col gap-2 rounded-40 border-[1.5px] border-grey-100 bg-surface-white px-6 py-10 text-center outline-none',
        className,
      )}
      {...props}
    />
  </AlertDialogPrimitive.Portal>
));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

/** 제목 + 본문 묶음 (선택). Content 바로 아래에 Title · Description을 둬도 같은 간격(8)이 된다 */
const AlertDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-2', className)} {...props} />
);

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn('text-subtitle-16-b text-fg-primary', className)}
    {...props}
  />
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn('text-body-14-m text-fg-tertiary', className)}
    {...props}
  />
));
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName;

/** 버튼 줄 — 위 본문과 24 간격 (Content gap 8 + mt 16) */
const AlertDialogFooter = ({
  className,
  layout = 'horizontal',
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  layout?: 'horizontal' | 'vertical';
}) => (
  <div
    className={cn(
      'mt-4 flex gap-2',
      layout === 'horizontal' ? '[&>*]:flex-1' : '-mb-4 flex-col',
      className,
    )}
    {...props}
  />
);

/** 주 버튼 — 검정 Large */
const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    className={cn(
      buttonVariants({ variant: 'primary', size: 'large' }),
      className,
    )}
    {...props}
  />
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

/** 보조 버튼 — 회색 Large (세로 배치에서는 className으로 text · medium 모양을 준다) */
const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(
      buttonVariants({ variant: 'grey', size: 'large' }),
      className,
    )}
    {...props}
  />
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
};
